import NfcIcon from '@mui/icons-material/Nfc';
import { Box, Typography, keyframes } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ScanWarningDialog } from '@ui/Components/Scan/ScanWarningDialog';
import { buildNfcResultState } from '@ui/Utils/scan/tag/scanTypes';

// Single slower pulse ring animation while scanning
const pulse = keyframes`
    0% { transform: scale(0.65); opacity: 0.55; }
    60% { transform: scale(1.15); opacity: 0.15; }
    100% { transform: scale(1.35); opacity: 0; }
`;

interface NfcScanProps {
    cancel: () => void;
    checkSupport: () => Promise<boolean>;
    compact?: boolean;
    isAllowed: boolean | null;
    lastError: string | null;
    lastTag?: Record<string, unknown> | null;
    lastUid?: string | null;
    lastUrl?: string | null;
    redirectUrl: string | null;
    scanning: boolean;
    scanTag: () => Promise<void>;
    supported: boolean | null;
}

export function NfcScan({
    cancel,
    checkSupport,
    compact,
    isAllowed,
    lastError,
    lastTag,
    lastUid,
    lastUrl,
    redirectUrl,
    scanning,
    scanTag,
    supported,
}: Readonly<NfcScanProps>) {
    const [infoOpen, setInfoOpen] = useState(false);
    const navigate = useNavigate();
    const closeInfo = useCallback(() => setInfoOpen(false), []);

    useEffect(() => {
        void checkSupport();
    }, [checkSupport]);

    const toggleScan = useCallback(() => {
        if (scanning) {
            cancel();
            return;
        }
        void scanTag();
    }, [scanTag, scanning, cancel]);

    const disabled = supported === false;

    const currentColor = (() => {
        if (scanning) {
            return 'primary.main';
        }
        if (disabled) {
            return 'text.disabled';
        }
        if (isAllowed === true) {
            return 'success.main';
        }
        if (isAllowed === false) {
            return 'error.main';
        }
        return 'text.disabled';
    })();

    // Navigate to unified result page on any terminal outcome
    useEffect(() => {
        if (!isAllowed && !lastError && !redirectUrl) {
            return; // nothing to do yet
        }
        // Derive advanced data
        let tech: string[] | undefined;
        let texts: { lang: string; text: string }[] | undefined;
        if (lastTag && typeof lastTag === 'object') {
            const arr = (lastTag as { kind?: unknown }).kind;
            if (Array.isArray(arr)) {
                tech = arr.filter((x): x is string => typeof x === 'string');
            }
            const recs = (lastTag as { records?: unknown }).records;
            if (Array.isArray(recs)) {
                texts = [];
                for (const r of recs) {
                    if (!r || typeof r !== 'object') {
                        continue;
                    }
                    const ro = r as Record<string, unknown> & { kind?: unknown; payload?: unknown; tnf?: unknown };
                    const kindField = ro.kind;
                    const tnf = ro.tnf;
                    if (Array.isArray(kindField) && kindField.length === 1 && kindField[0] === 0x54 && tnf === 1) {
                        const payload = ro.payload;
                        if (Array.isArray(payload) && payload.every((x) => typeof x === 'number') && payload.length) {
                            const bytes = payload;
                            const statusByte = bytes[0];
                            if (typeof statusByte === 'number') {
                                const langLen = statusByte & 0x3f;
                                const isUtf16 = (statusByte & 0x80) !== 0;
                                const langBytes = bytes.slice(1, 1 + langLen);
                                const textBytes = bytes.slice(1 + langLen);
                                const lang = langBytes.map((b) => String.fromCharCode(b & 0xff)).join('');
                                let decoded: string;
                                try {
                                    decoded = new TextDecoder(isUtf16 ? 'utf-16' : 'utf8').decode(
                                        new Uint8Array(textBytes.map((b) => b & 0xff)),
                                    );
                                } catch {
                                    decoded = textBytes.map((b) => String.fromCharCode(b & 0xff)).join('');
                                }
                                texts.push({ lang, text: decoded });
                            }
                        }
                    }
                }
                if (!texts.length) {
                    texts = undefined;
                }
            }
        }
        const stateBase = buildNfcResultState({
            error: isAllowed === false ? (lastError ?? 'Unsupported tag') : (lastError ?? null),
            ok: Boolean(isAllowed === true && redirectUrl),
            raw: redirectUrl ?? lastUrl ?? null,
            redirectUrl: redirectUrl,
            tech: tech ?? null,
            texts: texts ?? null,
            uid: lastUid ?? null,
        });
        if (stateBase.ok) {
            navigate('/scan/result', { state: stateBase });
        } else if (lastError || isAllowed === false) {
            navigate('/scan/result', { state: { ...stateBase, ok: false } });
        }
    }, [isAllowed, redirectUrl, navigate, lastError, lastUid, lastTag, lastUrl]);

    let statusText: string;
    if (supported === false) {
        statusText = 'NFC not available';
    } else if (scanning) {
        statusText = 'Scanning… hold near tag';
    } else if (isAllowed === true && redirectUrl) {
        statusText = 'Tag OK';
    } else if (isAllowed === false) {
        statusText = 'Unsupported tag';
    } else {
        statusText = 'Tap to scan';
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                justifyContent: 'center',
                py: 4,
            }}
        >
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant={compact ? 'subtitle1' : 'h6'}>{statusText}</Typography>
            </Box>
            <Box
                onClick={disabled ? undefined : toggleScan}
                role="button"
                sx={{
                    '&:active': {
                        transform: disabled ? 'none' : 'scale(0.95)',
                    },
                    alignItems: 'center',
                    bgcolor: disabled ? 'action.disabledBackground' : 'common.white',
                    border: '4px solid',
                    borderColor: currentColor,
                    borderRadius: '50%',
                    boxShadow: 4,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    height: compact ? 160 : 200,
                    justifyContent: 'center',
                    opacity: disabled ? 0.6 : 1,
                    overflow: 'hidden',
                    pointerEvents: disabled ? 'none' : 'auto',
                    position: 'relative',
                    transition: 'box-shadow 0.25s, transform 0.25s, opacity 0.25s',
                    userSelect: 'none',
                    width: compact ? 160 : 200,
                }}
            >
                {scanning ? (
                    <Box
                        sx={{
                            animation: `${pulse} 3s ease-out 0s infinite`,
                            border: '4px solid',
                            borderColor: currentColor,
                            borderRadius: '50%',
                            inset: 0,
                            position: 'absolute',
                        }}
                    />
                ) : null}
                <NfcIcon
                    sx={{
                        color: currentColor,
                        fontSize: compact ? 64 : 92,
                    }}
                />
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {lastError ? (
                    <Typography color="error" variant="caption">
                        {lastError}
                    </Typography>
                ) : null}
                {!lastError && isAllowed === false ? (
                    <Typography
                        sx={{
                            color: 'warning.main',
                        }}
                        variant="caption"
                    >
                        This tag is not supported
                    </Typography>
                ) : null}
            </Box>
            {/* Actions removed – navigation now handles outcomes */}
            <ScanWarningDialog onClose={closeInfo} open={infoOpen} />
        </Box>
    );
}
